import { action, computed, makeObservable, observable } from "mobx";
import { Option, IOptionObject, INumericalIdOptionObject } from "./Option";
import { UniqueIdentification } from "../../logic/UniqueIdentification";

export type Identity = string | number;
export type IdentifiableObject<T extends string> = { [key in T]: Identity };
export type ObjectContainingIdentifier<T extends string> = Partial<{ [key: string]: any }> & IdentifiableObject<T>;

export type StringIdentifiedOptionCollection<T extends IOptionObject> = OptionCollection<'value', IOptionObject, T>;
export type NumericallyIdentifiedOptionCollection<T extends INumericalIdOptionObject> = OptionCollection<'id', INumericalIdOptionObject, T>;

/*** Option Collection ***/

interface IOptionCollection<IdentityKey, T> {
    options: Option<T>[];
    identifier: IdentityKey;
}

export class OptionCollection<
    IdentityKey extends string = 'id',
    MinimallyIdentifiableObject extends IdentifiableObject<IdentityKey> = ObjectContainingIdentifier<IdentityKey>,
    OptionObject extends MinimallyIdentifiableObject = MinimallyIdentifiableObject
    >
    implements IOptionCollection<IdentityKey, OptionObject>
{
    @observable options: Option<OptionObject>[];
    @observable identifier: IdentityKey;

    constructor(identifierOrOptionCollection: IdentityKey, optionObjects: OptionObject[], selectedOptions?: MinimallyIdentifiableObject[]);
    constructor(identifierOrOptionCollection: OptionCollection<IdentityKey, MinimallyIdentifiableObject, OptionObject>);

    constructor(identifierOrOptionCollection: IdentityKey | OptionCollection<IdentityKey, MinimallyIdentifiableObject, OptionObject>, optionObjects?: OptionObject[], selectedOptions?: MinimallyIdentifiableObject[]) {
        makeObservable(this);

        if (typeof identifierOrOptionCollection === 'string') {
            this.identifier = identifierOrOptionCollection;
            this.options = optionObjects
                ? optionObjects.map(object => {
                    const selected = selectedOptions
                        ? selectedOptions.findIndex(selection => selection[this.identifier] === object[this.identifier]) !== -1
                        : false;
                    return new Option(object, selected);
                })
                : [];
        } else {
            identifierOrOptionCollection = identifierOrOptionCollection as OptionCollection<IdentityKey, MinimallyIdentifiableObject, OptionObject>;
            this.identifier = identifierOrOptionCollection.identifier;
            this.options = identifierOrOptionCollection.options.map(option => new Option(option.object, option.selected));
        }
    }

    @action setSelections(selections: MinimallyIdentifiableObject[]) {
        this.options.forEach(option => {
            const selected = selections.findIndex(selection => selection[this.identifier] === option.object[this.identifier]) !== -1;
            if (option.selected !== selected) {
                option.toggleSelection();
            }
        })
    }

    @action setSelectionsById(selections: Identity[]) {
        this.options.forEach(option => {
            const selected = selections.findIndex(selection => selection === option.object[this.identifier]) !== -1;
            if (option.selected !== selected) {
                option.toggleSelection();
            }
        })
    }

    @action push(toAdd: OptionObject | OptionObject[], selected?: boolean) {
        if (Array.isArray(toAdd)) {
            toAdd.forEach(optionObject => {
                this.addOption(optionObject, selected);
            });
        } else {
            this.addOption(toAdd, selected);
        }
    }

    @action addOrUpdateOption(optionObject: OptionObject, selected?: boolean) {
        const optionIndex = this.getOptionIndex(optionObject);
        if (optionIndex === -1) {
            this.addOption(optionObject, selected);
        } else {
            this.options[optionIndex] = new Option(optionObject, selected !== undefined ? selected : false);
        }
    }

    @action private addOption(optionObject: OptionObject, selected?: boolean) {
        const identifier = optionObject[this.identifier];
        if (typeof identifier === 'number' && identifier < 0) {
            this.assignUniqueClientId(optionObject);
        }
        const newOption = new Option(optionObject, selected !== undefined ? selected : false);
        this.options.push(newOption);
    }

    @action toggleSelection(optionObject: MinimallyIdentifiableObject) {
        const optionIndex = this.getOptionIndex(optionObject);
        if (optionIndex !== -1) {
            this.options[optionIndex].selected = !this.options[optionIndex].selected;
        }
    }

    @action clearOptions() {
        this.options = [];
    }

    @action removeOption(optionObject: OptionObject) {
        const optionIndex = this.getOptionIndex(optionObject);
        if (optionIndex !== -1) {
            this.options.splice(optionIndex, 1);
        }
    }

    @action removeOptionById(identifierValue: number | string) {
        const option = this.getOptionByIdentifier(identifierValue);
        if (!option) return;
        this.removeOption(option.object);
    }

    @action removeSelectedOptions() {
        for (let index = this.options.length - 1; index >= 0; index--) {
            if (this.options[index].selected) {
                this.options.splice(index, 1);
            }
        }
    }

    @action splice(start: number, deleteCount: number, ...items: Option<OptionObject>[]) {
        this.options.splice(start, deleteCount, ...items);
    }

    @action replaceOption(oldOption: OptionObject, replacement: OptionObject) {
        const optionIndex = this.getOptionIndex(oldOption);
        if (optionIndex !== -1) {
            this.options.splice(optionIndex, 1, new Option(replacement, false));
        }
    }

    getOptionByIdentifier(identifierValue: number | string) {
        return this.options.find(option => option.object[this.identifier] === identifierValue);
    }

    isValidOption(optionObject: OptionObject) {
        return this.getOptionIndex(optionObject) !== -1;
    }

    private getOptionIndex(optionObject: MinimallyIdentifiableObject) {
        return this.options.findIndex(existingOption => existingOption.object[this.identifier] === optionObject[this.identifier]);
    }

    @action private assignUniqueClientId(optionObject: OptionObject) {
        if (typeof optionObject[this.identifier] === 'number') {
            (optionObject[this.identifier] as number) = UniqueIdentification.getClientId();
        }
    }

    @computed get selections(): Option<OptionObject>[] {
        return this.options.filter(option => option.selected);
    }

    @computed get optionObjects(): OptionObject[] {
        return this.options.map(option => option.object);
    }

    @computed get selectedOptions(): OptionObject[] {
        return this.selections.map(selection => selection.object);
    }

    isOptionSelected(option: MinimallyIdentifiableObject[IdentityKey]) {
        return this.selectedOptions.findIndex(selection => selection[this.identifier] === option) !== -1;
    }

    isOptionWithIdentifierSelected(identifierValue: number | string) {
        return this.selectedOptions.findIndex(selection => selection[this.identifier] === identifierValue) !== -1;
    }
}